import { useHandler } from "@redotech/react-util/hook";
import BarChartIcon from "@redotech/redo-icons/bar-chart-12.svg";
import LineChartIcon from "@redotech/redo-icons/line-chart-up-01_filled.svg";
import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartType,
  DefaultDataPoint,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { RedoButton } from "../../buttons/redo-button";
import {
  defaultDateFormatter,
  defaultValueFormatter,
  getChartOptions,
} from "./chart-options";
import { crosshairPlugin } from "./crosshair-plugin";
import { ChartLegend } from "./legend";
import * as redoLineAndBarChartCss from "./redo-line-and-bar-chart.module.css";
import {
  ChartProcessedData,
  ChartReferenceLine,
  RedoChartStyle,
  RedoLineAndBarChartProps,
} from "./types";
import { useChartData } from "./useChartData";

type ChartJSOrUndefined<
  TType extends ChartType = ChartType,
  TData = DefaultDataPoint<TType>,
  TLabel = unknown,
> = ChartJS<TType, TData, TLabel> | undefined;

function findClosestIndex(
  refTime: number,
  seriesDates: Array<{ date: Temporal.Instant }>,
): number {
  let closestIndex = -1;
  let closestDiff = Infinity;

  for (const [i, seriesDate] of seriesDates.entries()) {
    const dateTime = seriesDate.date.epochMilliseconds;
    const diff = Math.abs(dateTime - refTime);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = i;
    }
  }

  return closestIndex;
}

function createReferenceLinesPlugin(
  referenceLines: ChartReferenceLine[] | undefined,
  seriesDates: Array<{ date: Temporal.Instant }>,
) {
  if (!referenceLines?.length || seriesDates.length === 0) {
    return null;
  }

  return {
    id: "referenceLinesPlugin",
    afterDraw(chart: ChartJS) {
      for (const [index, line] of referenceLines.entries()) {
        const refTime = line.date.epochMilliseconds;
        const closestIndex = findClosestIndex(refTime, seriesDates);
        if (closestIndex !== -1) {
          drawReferenceLine(chart, closestIndex, line, index);
        }
      }
    },
  };
}

function drawReferenceLine(
  chart: ChartJS,
  labelIndex: number,
  referenceLine: ChartReferenceLine,
  lineIndex: number,
) {
  const { ctx, chartArea, scales } = chart;
  if (!chartArea || !scales.x) {
    return;
  }

  const x = scales.x.getPixelForValue(labelIndex);
  const { top, bottom } = chartArea;

  ctx.save();
  ctx.beginPath();
  ctx.setLineDash(referenceLine.dashPattern ?? [5, 5]);
  ctx.strokeStyle = referenceLine.color ?? "#dc2626";
  ctx.lineWidth = 2;
  ctx.moveTo(x, top);
  ctx.lineTo(x, bottom);
  ctx.stroke();
  ctx.restore();

  if (referenceLine.label) {
    ctx.save();
    ctx.fillStyle = referenceLine.color ?? "#dc2626";
    ctx.font = "11px sans-serif";
    ctx.textAlign = "left";
    const yOffset = 14 + lineIndex * 14;
    ctx.fillText(referenceLine.label, x + 4, top + yOffset);
    ctx.restore();
  }
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  crosshairPlugin,
);

const loadingData: ChartProcessedData = {
  labels: [""],
  datasets: [
    {
      label: "",
      data: [0],
      borderColor: "transparent",
      backgroundColor: "transparent",
    },
  ],
  normalizedSeries: [],
};

export const RedoLineAndBarChart = memo(function RedoLineAndBarChart({
  series,
  leftAxisLabel,
  bottomAxisLabel,
  chartStyle: initialChartStyle = "line",
  legendPosition,
  dateFormatter = defaultDateFormatter,
  valueAxisFormatter = defaultValueFormatter,
  valueTooltipFormatter = defaultValueFormatter,
  titleText,
  allowGraphTypeSwitch = false,
  showTooltipSeriesNames = false,
  customTooltip,
  customTooltipTitles,
  loading = false,
  referenceLines,
  showPoints = true,
  disableGradient = false,
  leftAxisPrecision,
  emphasizeZeroLine = false,
}: RedoLineAndBarChartProps) {
  const [internalChartStyle, setInternalChartStyle] =
    useState<RedoChartStyle>(initialChartStyle);
  const [hoveredTooltip, setHoveredTooltip] = useState<{
    content: string;
    x: number;
    y: number;
  } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const chartRef = useRef<ChartJSOrUndefined<"line" | "bar">>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const chartStyle = allowGraphTypeSwitch
    ? internalChartStyle
    : initialChartStyle;

  const actualProcessedData = useChartData(
    series,
    chartStyle,
    dateFormatter,
    disableGradient,
    showPoints,
  );

  const processedData = loading ? loadingData : actualProcessedData;

  const seriesDates = useMemo(
    () => processedData.normalizedSeries[0]?.data ?? [],
    [processedData.normalizedSeries],
  );

  const referenceLinesPlugin = useMemo(
    () => createReferenceLinesPlugin(referenceLines, seriesDates),
    [referenceLines, seriesDates],
  );

  useEffect(() => {
    if (!hoveredTooltip || !tooltipRef.current) {
      setTooltipPosition(null);
      return;
    }

    const tooltipWidth = tooltipRef.current.offsetWidth;
    const offset = 10;
    const viewportWidth = window.innerWidth;

    let left = hoveredTooltip.x + offset;

    if (left + tooltipWidth > viewportWidth - offset) {
      left = hoveredTooltip.x - tooltipWidth - offset;
    }

    setTooltipPosition({ left, top: hoveredTooltip.y - 10 });
  }, [hoveredTooltip]);

  const plugins = useMemo(
    () => (referenceLinesPlugin ? [referenceLinesPlugin] : []),
    [referenceLinesPlugin],
  );

  const handleMouseMove = useHandler(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const chart = chartRef.current;
      if (!chart || !referenceLines?.length || seriesDates.length === 0) {
        setHoveredTooltip(null);
        return;
      }

      const { chartArea, scales } = chart;
      if (!chartArea || !scales.x) {
        setHoveredTooltip(null);
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const hitThreshold = 10;

      for (const line of referenceLines) {
        const refTime = line.date.epochMilliseconds;
        const closestIndex = findClosestIndex(refTime, seriesDates);
        if (closestIndex === -1) {
          continue;
        }

        const lineX = scales.x.getPixelForValue(closestIndex);
        if (Math.abs(mouseX - lineX) <= hitThreshold && line.tooltip) {
          setHoveredTooltip({
            content: line.tooltip,
            x: event.clientX,
            y: event.clientY,
          });
          return;
        }
      }

      setHoveredTooltip(null);
    },
  );

  const handleMouseLeave = useHandler(() => {
    setHoveredTooltip(null);
  });

  const baseOptions = getChartOptions({
    leftAxisLabel,
    bottomAxisLabel,
    valueAxisFormatter,
    valueTooltipFormatter,
    chartStyle,
    showSeriesNames: showTooltipSeriesNames,
    customTooltip,
    series: processedData.normalizedSeries,
    customTooltipTitles,
    leftAxisPrecision,
    emphasizeZeroLine,
  });

  const options = {
    ...baseOptions,
    maintainAspectRatio: false,
    responsive: true,
    ...(loading && {
      scales: {
        ...baseOptions.scales,
        y: { ...baseOptions.scales?.y, max: 100 },
      },
    }),
  };

  return (
    <Flex dir="column" height="full" w="full">
      {(titleText || allowGraphTypeSwitch || legendPosition === "top") && (
        <Flex
          align="center"
          justify={titleText ? "space-between" : "flex-end"}
          mb="md"
          w="full"
        >
          {titleText && (
            <Text fontSize="sm" fontWeight="medium" textColor="tertiary">
              {titleText}
            </Text>
          )}

          <Flex>
            {legendPosition === "top" && (
              <ChartLegend
                position={legendPosition}
                series={processedData.normalizedSeries}
              />
            )}
            {allowGraphTypeSwitch && (
              <Flex gap="xs">
                <RedoButton
                  aria-label="Line chart"
                  buttonClassName={
                    chartStyle === "line"
                      ? redoLineAndBarChartCss.chartControlButtonActive
                      : ""
                  }
                  hierarchy="tertiary"
                  IconTrailing={LineChartIcon}
                  onClick={() => setInternalChartStyle("line")}
                  size="sm"
                />
                <RedoButton
                  aria-label="Bar chart"
                  buttonClassName={
                    chartStyle === "bar"
                      ? redoLineAndBarChartCss.chartControlButtonActive
                      : ""
                  }
                  hierarchy="tertiary"
                  IconTrailing={BarChartIcon}
                  onClick={() => setInternalChartStyle("bar")}
                  size="sm"
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      )}

      <Flex grow={1} height="full" w="full">
        <Flex
          className={redoLineAndBarChartCss.chartContainer}
          height="full"
          minW="none"
          position="relative"
          w="full"
        >
          <div
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
            style={{ width: "100%", height: "100%" }}
          >
            {chartStyle === "line" ? (
              <Line
                data={processedData as ChartData<"line">}
                options={options}
                plugins={plugins}
                ref={chartRef as React.Ref<ChartJSOrUndefined<"line">>}
              />
            ) : (
              <Bar
                data={processedData as ChartData<"bar">}
                options={options}
                plugins={plugins}
                ref={chartRef as React.Ref<ChartJSOrUndefined<"bar">>}
              />
            )}
          </div>
          {hoveredTooltip && (
            <div
              className={redoLineAndBarChartCss.referenceLineTooltip}
              ref={tooltipRef}
              style={{
                position: "fixed",
                left: tooltipPosition?.left ?? hoveredTooltip.x + 10,
                top: tooltipPosition?.top ?? hoveredTooltip.y - 10,
                visibility: tooltipPosition ? "visible" : "hidden",
              }}
            >
              <Text fontSize="xs">{hoveredTooltip.content}</Text>
            </div>
          )}
        </Flex>

        {legendPosition === "right" && (
          <ChartLegend
            position={legendPosition}
            series={processedData.normalizedSeries}
          />
        )}
      </Flex>

      {legendPosition === "bottom" && (
        <ChartLegend
          position={legendPosition}
          series={processedData.normalizedSeries}
        />
      )}
    </Flex>
  );
});
